'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import * as React from 'react';
import { FormProvider, useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetupConnectFormActions } from '@/components/setup/experience/SetupConnectFormShell';
import { SetupConnectStepSection } from '@/components/setup/experience/SetupConnectStepSection';
import { SetupShippingRateCard } from '@/components/setup/experience/SetupShippingRateCard';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import {
  createSetupShippingRatesSchema,
  type SetupShippingRatesFormValues,
} from '@/components/setup/experience/lib/setupShippingRateSchema';
import {
  createDefaultSetupShippingRates,
  createEmptySetupShippingRateDraft,
  type SetupShippingRateDraft,
} from '@/components/setup/experience/lib/setupShippingRateTypes';
import {
  buildSetupShippingRateInput,
  buildSetupShippingRateUpdateInput,
  isSameSetupShippingDraft,
} from '@/components/setup/experience/lib/setupShippingRateUtils';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/api/trpc';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';

interface SetupShippingRatesFormProps {
  storeId: string;
  baseCurrency: string;
  /** Persisted rates mapped to drafts — empty on a fresh store (a default rate is seeded instead). */
  initialRates: SetupShippingRateDraft[];
  /** Whether the store already had rates before this step (drives the "highlighted" flag on creates). */
  hadExistingRates: boolean;
  onBack: () => void;
  /** Rates saved (or unchanged) → advance to payment. */
  onContinue: () => void;
}

export function SetupShippingRatesForm({
  storeId,
  baseCurrency,
  initialRates,
  hadExistingRates,
  onBack,
  onContinue,
}: SetupShippingRatesFormProps) {
  const { t } = useTranslation(['setup', 'shippings']);
  const xp = 'experience.shipping';
  const utils = trpc.useUtils();
  const invalidateList = () => void utils.shippingRates.list.invalidate();
  const createShippingRate = trpc.shippingRates.create.useMutation({ onSuccess: invalidateList });
  const updateShippingRate = trpc.shippingRates.update.useMutation({ onSuccess: invalidateList });
  const deleteShippingRate = trpc.shippingRates.delete.useMutation({ onSuccess: invalidateList });

  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);
  const rateListRef = React.useRef<HTMLDivElement>(null);

  const schema = React.useMemo(
    () =>
      createSetupShippingRatesSchema({
        nameRequired: t(`${xp}.nameRequired`),
        priceRequired: t(`${xp}.priceRequired`),
        priceInvalid: t(`${xp}.priceInvalid`),
        countriesRequired: t(`${xp}.countriesRequired`),
        ratesMin: t(`${xp}.ratesMin`),
      }),
    [t],
  );

  const defaultRates = initialRates.length ? initialRates : createDefaultSetupShippingRates(t).rates;

  const methods = useForm<SetupShippingRatesFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { rates: defaultRates },
    mode: 'onTouched',
  });

  // Baseline keyed by DB id — lets the submit skip rates the user never touched. Newly created rates
  // are added here (with their fresh id) so a retry after a partial failure never duplicates them.
  const baselineRef = React.useRef<Map<string, SetupShippingRateDraft> | null>(null);
  if (baselineRef.current === null) {
    baselineRef.current = new Map(
      initialRates.filter((rate) => rate.id).map((rate) => [rate.id as string, rate]),
    );
  }

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray<SetupShippingRatesFormValues, 'rates', 'rhfKey'>({
    control,
    name: 'rates',
    keyName: 'rhfKey',
  });

  const scrollRateIntoView = React.useCallback((index: number) => {
    requestAnimationFrame(() => {
      const item = rateListRef.current?.children.item(index) as HTMLElement | null;
      item?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, []);

  const handleAddRate = () => {
    const nextIndex = fields.length;
    append(createEmptySetupShippingRateDraft());
    scrollRateIntoView(nextIndex);
  };

  /**
   * Reconcile the DB to the form on continue: persisted rates the user removed are deleted, drafts
   * without an id are created, touched persisted rates are updated, untouched ones are skipped. Each
   * create writes its id back into the form so a retry after a mid-loop failure resumes cleanly
   * instead of re-creating. Deletion is deferred here (not on the trash click) so going back without
   * continuing leaves the rate intact.
   */
  const onSubmit = async (data: SetupShippingRatesFormValues) => {
    setFormError(null);

    const baseline = baselineRef.current!;

    try {
      const presentIds = new Set(data.rates.map((rate) => rate.id).filter(Boolean));
      const removedIds = [...baseline.keys()].filter((id) => !presentIds.has(id));
      if (removedIds.length) {
        await deleteShippingRate.mutateAsync({ ids: removedIds });
        removedIds.forEach((id) => baseline.delete(id));
      }

      for (const [index, rate] of data.rates.entries()) {
        if (!rate.id) {
          const highlighted = !hadExistingRates && index === 0;
          const result = await createShippingRate.mutateAsync(
            buildSetupShippingRateInput(storeId, baseCurrency, rate, highlighted),
          );
          setValue(`rates.${index}.id`, result.id);
          baseline.set(result.id, { ...rate, id: result.id });
          continue;
        }

        const previous = baseline.get(rate.id);
        if (!previous || !isSameSetupShippingDraft(previous, rate)) {
          await updateShippingRate.mutateAsync(
            buildSetupShippingRateUpdateInput(rate.id, baseCurrency, rate),
          );
          baseline.set(rate.id, { ...rate });
        }
      }
    } catch (error) {
      setFormError(parseTRPCError(error));
      return;
    }

    onContinue();
  };

  const onInvalid = (formErrors: FieldErrors<SetupShippingRatesFormValues>) => {
    if (Array.isArray(formErrors.rates)) {
      const firstInvalid = formErrors.rates.findIndex(Boolean);
      if (firstInvalid >= 0) scrollRateIntoView(firstInvalid);
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col">
        <motion.form
          className="flex w-full max-w-none flex-col gap-0"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          noValidate
          initial={SETUP_PATH_MOTION.actions.initial}
          animate={SETUP_PATH_MOTION.actions.animate}
          transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.1 }}
        >
          <SetupConnectStepSection
            title={t('experience.shell.stepTitles.shipping')}
            description={t('experience.shell.stepDescriptions.shipping')}
            footer={
              <SetupConnectFormActions
                backLabel={t('footer.back')}
                onBack={onBack}
                submitLabel={t('footer.continue')}
                isSubmitting={isSubmitting}
              />
            }
          >
            <fieldset disabled={isSubmitting} className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0 pb-4">
              <div
                ref={rateListRef}
                className="flex flex-col divide-y divide-[color-mix(in_srgb,var(--border)_70%,transparent)]"
              >
                {fields.map((field, index) => (
                  <SetupShippingRateCard
                    key={field.rhfKey}
                    index={index}
                    currency={baseCurrency}
                    // Keep at least one rate — the step seeds a default and the schema enforces a minimum.
                    canRemove={fields.length > 1}
                    onRemove={remove}
                  />
                ))}
              </div>

              <div className="border-t border-[color-mix(in_srgb,var(--border)_70%,transparent)] pt-5">
                <Button type="button" variant="outline" onClick={handleAddRate} disabled={isSubmitting}>
                  <Plus className="size-4" aria-hidden />
                  {t(`${xp}.addRate`)}
                </Button>
              </div>

              {errors.rates && !Array.isArray(errors.rates) ? (
                <p
                  className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
                  role="alert"
                >
                  {errors.rates.message}
                </p>
              ) : null}

              {formError ? (
                <p
                  className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
                  role="alert"
                >
                  {formError.message}
                </p>
              ) : null}
            </fieldset>
          </SetupConnectStepSection>
        </motion.form>
      </div>
    </FormProvider>
  );
}
